import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Button, Box, Grid, Typography, FormControl, InputLabel, Select, MenuItem, FormHelperText, TextField } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useNavigate, useParams } from 'react-router-dom';
import { useDepartmentEmployeelistQuery, useAdminCreateApiMutation } from './../../../service/apiSlices/accessApiService';
import { toast } from 'react-toastify';
import { TOASTER_CONFIGS } from '../../../constants';

interface Employee {
  id: number;
  fullname: string;
  email: string;
}

const CreateAdmin = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: DepAndEmployeeList } = useDepartmentEmployeelistQuery('');

  const [initialValues, setInitialValues] = useState({
    employee_id: '',
    department_id: '',
  });

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployeeEmail, setSelectedEmployeeEmail] = useState('');

  const validationSchema = Yup.object({
    department_id: Yup.string().required('Required'),
    employee_id: Yup.string().required('Required'),
  });

  const [adminCreateApiCall] = useAdminCreateApiMutation();

  const PostAdminCreateApiCall = async (values: any) => {
    const payload = {
      department_id: values.department_id,
      employee_id: values.employee_id,
    };

    await adminCreateApiCall(payload)
      .unwrap()
      .then((response) => {
        toast.success(response?.message, TOASTER_CONFIGS);
        navigate(-1)
      })
      .catch((error) => {
        toast.error(error?.data?.message, TOASTER_CONFIGS);
        console.error(error, 'API Error');
      });
  };

  const handleApicall = (values: any) => {
    PostAdminCreateApiCall(values);
  };

  useEffect(() => {
    if (initialValues.department_id) {
      const selectedDepartment = DepAndEmployeeList?.result?.data?.find(
        (dept: any) => dept.id === initialValues.department_id
      );
      setEmployees(selectedDepartment ? selectedDepartment.employee : []);
    } else {
      setEmployees([]);
    }
  }, [initialValues.department_id, DepAndEmployeeList]);

  useEffect(() => {
    if (initialValues.employee_id) {
      const selectedEmployee = employees.find((emp: any) => emp.id === initialValues.employee_id);
      setSelectedEmployeeEmail(selectedEmployee ? selectedEmployee.email : '');
    } else {
      setSelectedEmployeeEmail('');
    }
  }, [initialValues.employee_id, employees]);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, actions) => {
          handleApicall(values);
        }}
        enableReinitialize={true}
      >
        {(formik) => (
          <Box
            component="form"
            sx={{ '& .MuiTextField-root': { m: 1, width: '100%' } }}
            noValidate
            autoComplete="off"
            onSubmit={formik.handleSubmit}
          >
            <Grid
              container
              spacing={2}
              justifyContent="flex-start"
              alignContent='center'
              marginTop={1}
              marginBottom={3}
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate(-1)}
            >
              <KeyboardArrowLeftIcon />
              <Typography>
                {id ? 'Edit' : 'Add'} Admin
              </Typography>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ mt: 1, ml: 1 }}>
                  <InputLabel id="department-select-label">Category</InputLabel>
                  <Select
                    labelId="department-select-label"
                    id="department-select"
                    name="department_id"
                    value={formik.values.department_id}
                    onChange={(e) => {
                      formik.handleChange(e);
                      setInitialValues({
                        ...initialValues,
                        department_id: e.target.value,
                      });
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.department_id && Boolean(formik.errors.department_id)}
                    label="Category"
                  >
                    {DepAndEmployeeList?.result?.data.map((item: any) => (
                      <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                    ))}
                  </Select>
                  {formik.touched.department_id && formik.errors.department_id && (
                    <FormHelperText sx={{ color: '#d32f2f' }}>{formik.errors.department_id}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              {formik.values.department_id && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={{ mt: 1, ml: 1 }}>
                    <InputLabel id="employee-select-label">Employee</InputLabel>
                    <Select
                      labelId="employee-select-label"
                      id="employee-select"
                      name="employee_id"
                      value={formik.values.employee_id}
                      onChange={(e) => {
                        formik.handleChange(e);
                        setInitialValues({
                          ...initialValues,
                          employee_id: e.target.value,
                        });
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.employee_id && Boolean(formik.errors.employee_id)}
                      label="Employee"
                    >
                      {employees.map((employee) => (
                        <MenuItem key={employee.id} value={employee.id}>{employee.fullname}</MenuItem>
                      ))}
                    </Select>
                    {formik.touched.employee_id && formik.errors.employee_id && (
                      <FormHelperText sx={{ color: '#d32f2f' }}>{formik.errors.employee_id}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              )}

              {selectedEmployeeEmail && (
                <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                  <FormControl fullWidth sx={{ mt: 1, ml: 1 }}>
                    {/* <InputLabel>Email</InputLabel> */}
                    <TextField
                      value={selectedEmployeeEmail}
                      InputProps={{
                        readOnly: true,
                        sx: {
                          '& input': {
                            color: 'rgba(0, 0, 0, 0.6)',
                          },
                        },
                      }}
                      label="Employee Email"
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
              )}
            </Grid>

            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <Button
                  type="button"
                  variant="contained"
                  color="inherit"
                  onClick={() => navigate(-1)}
                  sx={{ mt: 3, mb: 2 }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ mt: 3, mb: 2, background: 'linear-gradient(to right, #00C6FF, #0072FF)' }}
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Formik>
    </>
  );
};

export default CreateAdmin;




















// import { useEffect, useState } from 'react';
// import { Formik } from 'formik';
// import * as Yup from 'yup';
// import { Button, Box, Grid, Typography, FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
// import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
// import { useNavigate, useParams } from 'react-router-dom';
// import { useDepartmentEmployeelistQuery, useAdminCreateApiMutation } from './../../../service/apiSlices/accessApiService';
// import { toast } from 'react-toastify';
// import { TOASTER_CONFIGS } from '../../../constants';

// const CreateAdmin = () => {
//   const navigate = useNavigate();
//   const { id } = useParams();
//   const { data: DepAndEmployeeList } = useDepartmentEmployeelistQuery('');

//   const [initialValues, setInitialValues] = useState({
//     employee_id: '',
//     department_id: '',
//   });

//   const [employees, setEmployees] = useState([]);

//   const validationSchema = Yup.object({
//     department_id: Yup.string().required('Required'),
//     employee_id: Yup.string().required('Required'),
//   });

//   const [adminCreateApiCall] = useAdminCreateApiMutation();

//   const PostAdminCreateApiCall = async (values: any) => {
//     const payload = {
//       department_id: values.department_id,
//       employee_id: values.employee_id,
//     };

//     await adminCreateApiCall(payload)
//       .unwrap()
//       .then((response) => {
//         toast.success(response?.message, TOASTER_CONFIGS);
//         navigate(-1)
//         console.log(response, 'API Response');
//       })
//       .catch((error) => {
//         toast.error(error?.data?.message, TOASTER_CONFIGS);
//         console.error(error, 'API Error');
//       });
//   };

//   const handleApicall = (values: any) => {
//     console.log(values, "form_values");
//     PostAdminCreateApiCall(values);
//   };

//   useEffect(() => {
//     if (initialValues.department_id) {
//       const selectedDepartment = DepAndEmployeeList?.result?.data?.find(
//         (dept: any) => dept.id === initialValues.department_id
//       );
//       setEmployees(selectedDepartment ? selectedDepartment.employee : []);
//     } else {
//       setEmployees([]);
//     }
//   }, [initialValues.department_id, DepAndEmployeeList]);

//   return (
//     <>
//       <Formik
//         initialValues={initialValues}
//         validationSchema={validationSchema}
//         onSubmit={(values, actions) => {
//           handleApicall(values);
//         }}
//         enableReinitialize={true}
//       >
//         {(formik) => (
//           <Box
//             component="form"
//             sx={{ '& .MuiTextField-root': { m: 1, width: '100%' } }}
//             noValidate
//             autoComplete="off"
//             onSubmit={formik.handleSubmit}
//           >
//             <Grid
//               container
//               spacing={2}
//               justifyContent="flex-start"
//               alignContent='center'
//               marginTop={1}
//               marginBottom={3}
//               sx={{ cursor: 'pointer' }}
//               onClick={() => navigate(-1)}
//             >
//               <KeyboardArrowLeftIcon />
//               <Typography>
//                 {id ? 'Edit' : 'Add'} Admin
//               </Typography>
//             </Grid>
//             <Grid container spacing={2}>
//               <Grid item xs={12} sm={6}>
//                 <FormControl fullWidth sx={{ mt: 1, ml: 1 }}>
//                   <InputLabel id="department-select-label">Department</InputLabel>
//                   <Select
//                     labelId="department-select-label"
//                     id="department-select"
//                     name="department_id"
//                     value={formik.values.department_id}
//                     onChange={(e) => {
//                       formik.handleChange(e);
//                       setInitialValues({
//                         ...initialValues,
//                         department_id: e.target.value,
//                       });
//                     }}
//                     onBlur={formik.handleBlur}
//                     error={formik.touched.department_id && Boolean(formik.errors.department_id)}
//                     label="Department"
//                   >
//                     {DepAndEmployeeList?.result?.data.map((item: any) => (
//                       <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
//                     ))}
//                   </Select>
//                   {formik.touched.department_id && formik.errors.department_id && (
//                     <FormHelperText sx={{ color: '#d32f2f' }}>{formik.errors.department_id}</FormHelperText>
//                   )}
//                 </FormControl>
//               </Grid>
//               {formik.values.department_id && (
//                 <Grid item xs={12} sm={6}>
//                   <FormControl fullWidth sx={{ mt: 1, ml: 1 }}>
//                     <InputLabel id="employee-select-label">Employee</InputLabel>
//                     <Select
//                       labelId="employee-select-label"
//                       id="employee-select"
//                       name="employee_id"
//                       value={formik.values.employee_id}
//                       onChange={formik.handleChange}
//                       onBlur={formik.handleBlur}
//                       error={formik.touched.employee_id && Boolean(formik.errors.employee_id)}
//                       label="Employee"
//                     >
//                       {employees.map((employee: any) => (
//                         <MenuItem key={employee.id} value={employee.id}>{employee.fullname}</MenuItem>
//                       ))}
//                     </Select>
//                     {formik.touched.employee_id && formik.errors.employee_id && (
//                       <FormHelperText sx={{ color: '#d32f2f' }}>{formik.errors.employee_id}</FormHelperText>
//                     )}
//                   </FormControl>
//                 </Grid>
//               )}
//             </Grid>
//             <Grid container spacing={2} justifyContent="flex-end">
//               <Grid item>
//                 <Button
//                   type="button"
//                   variant="contained"
//                   color="inherit"
//                   onClick={() => navigate(-1)}
//                   sx={{ mt: 3, mb: 2 }}
//                 >
//                   Cancel
//                 </Button>
//               </Grid>
//               <Grid item>
//                 <Button
//                   type="submit"
//                   variant="contained"
//                   sx={{ mt: 3, mb: 2, background: 'linear-gradient(to right, #00C6FF, #0072FF)' }}
//                 >
//                   Submit
//                 </Button>
//               </Grid>
//             </Grid>
//           </Box>
//         )}
//       </Formik>
//     </>
//   );
// };

// export default CreateAdmin;